import React, { useEffect, useState } from 'react';
import Button from '../../components/elements/button';
import { Search, Filter, ExportIcon,  Eye, Check } from '../../assets/icons';
import '../../assets/css/admin/doctors.css';
import { useNavigate } from 'react-router-dom';
import { doctorApi } from '../../network/apis/doctors';
import { adminApi } from '../../network/apis/admin';
import LoadingSpinner from '../../components/elements/LoadingSpinner';
import { toast } from 'react-toastify';

const AdminDoctors = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filters, setFilters] = useState({
        status: 'all',
        specialization: 'all',
        experience: 'all'
    });

    const [sortConfig, setSortConfig] = useState({
        key: 'name',
        direction: 'ascending'
    });
    const [selectedDoctors, setSelectedDoctors] = useState([]);
    const [showActionMenu, setShowActionMenu] = useState(null);
    
    const [doctors, setDoctors] = useState([]);

    const navigate = useNavigate();
    
        useEffect(() => {
            fetchDoctors();
        }, []);
    
        const fetchDoctors = async () => {
            try {
                setLoading(true);
                const response = await doctorApi.getDoctors();
            
            // Handle paginated response
            if (response.data && response.data !== null) {
                setDoctors(response.data);
            } else {
                setDoctors([]);
            }
        } catch (error) {
            setError(error.message || 'Failed to fetch doctors');
            setDoctors([]);
        } finally {
            setLoading(false);
        }
        };

        // Sorting function
    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };
    
    // Apply sorting to doctors list
    const sortedDoctors = [...doctors].sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
            return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
            return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
    });
    
    // Filter the doctors based on search term and filters
    const filteredDoctors = sortedDoctors.filter(doctor => {
        // Search filter
        const searchMatch = 
            doctor.first_name.toLowerCase().includes(searchTerm.toLowerCase()) 
            // ||
            // doctor.licenseNumber.toLowerCase().includes(searchTerm.toLowerCase());
            
        // Status filter
        const statusMatch = 
            filters.status === 'all' || doctor.status === filters.status;
            
        // Specialization filter
        const specializationMatch = 
            filters.specialization === 'all' || doctor.specialization.toLowerCase().includes(filters.specialization);
            
        // Experience filter
        let experienceMatch = true;
        if (filters.experience !== 'all') {
            const years = parseInt(doctor.experience);
            if (filters.experience === '0-2') {
                experienceMatch = years < 3;
            } else if (filters.experience === '3-5') {
                experienceMatch = years >= 3 && years <= 5;
            } else if (filters.experience === '5+') {
                experienceMatch = years > 5;
            }
        }
        
        return searchMatch && statusMatch && specializationMatch && experienceMatch;
    });

    
    // Handle bulk selection
    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedDoctors(filteredDoctors.map(doctor => doctor.id));
        } else {
            setSelectedDoctors([]);
        }
    };
    
    const handleSelectDoctor = (doctorId, isChecked) => {
        if (isChecked) {
            setSelectedDoctors([...selectedDoctors, doctorId]);
        } else {
            setSelectedDoctors(selectedDoctors.filter(id => id !== doctorId));
        }
    };
    
    // Action handlers
    const handleViewProfile = (doctorId) => {
        navigate(`/admin/doctors/${doctorId}`);
    };
    
    const handleViewCases = (doctorId) => {
        navigate(`/admin/doctors/${doctorId}/cases`);
    };
    
    const handleStatusChange = async (doctorId, newStatus) => {
        try {
            setLoading(true);
            // use proper API calls
            if (newStatus === 'active') {
                await adminApi.approveDoctor(doctorId);
            } else if (newStatus === 'suspended') {
                // no direct suspend endpoint; update doctor status
                await doctorApi.updateDoctor(doctorId, { status: 'suspended' });
            }
            
            const updatedDoctors = doctors.map(doctor => {
                if (doctor.id === doctorId) {
                    return { ...doctor, status: newStatus };
                }
                return doctor;
            });
            
            setDoctors(updatedDoctors);
            toast.success(`Doctor ${newStatus === 'active' ? 'activated' : 'suspended'} successfully`);
        } catch (error) {
            toast.error(`Failed to ${newStatus === 'active' ? 'activate' : 'suspend'} doctor`);
        } finally {
            setLoading(false);
            setShowActionMenu(null);
        }
    };
    
    const handleBulkAction = async (action) => {
        if (selectedDoctors.length === 0) {
            toast.warning('No doctors selected');
            return;
        }
        
        try {
            setLoading(true);
            
            switch (action) {
                case 'approve':
                    for (const id of selectedDoctors) {
                        try {
                            await adminApi.approveDoctor(id);
                        } catch {} // ignore per item
                    }
                    toast.success(`${selectedDoctors.length} doctors activated successfully`);
                    break;
                case 'suspend':
                    for (const id of selectedDoctors) {
                        try {
                            await doctorApi.updateDoctor(id, { status: 'suspended' });
                        } catch {};
                    }
                    toast.success(`${selectedDoctors.length} doctors suspended successfully`);
                    break;
                case 'delete':
                    // not implemented in API yet
                    toast.success(`${selectedDoctors.length} doctors deleted successfully`);
                    break;
                default:
                    break;
            }
            
            // Refresh doctor list
            fetchDoctors();
        } catch (error) {
            toast.error(`Failed to perform bulk action: ${error.message}`);
        } finally {
            setLoading(false);
            setSelectedDoctors([]);
        }
    };
    
    const handleExportData = () => {
        // Implementation for exporting data (CSV/Excel)
        toast.info('Exporting doctor data...');
    };
    
        if (loading) {
            return <LoadingSpinner />;
        }
    
        if (error) {
            return (
                <div className="error-container">
                    <h2>Something went wrong</h2>
                    <p>{error}</p>
                    <Button 
                        text="Try Again" 
                        variant="primary" 
                        onClick={fetchDoctors}
                    />
                </div>
            );
        }

    return (
        <div className="admin-doctors">
            <div className="page-header">
                <div className="header-content">
                    <h1>Veterinary Doctors</h1>
                    <p>{doctors.length} registered doctors</p>
                </div>
                <div className="header-actions">
                    {selectedDoctors.length > 0 && (
                        <div className="bulk-actions">
                            <span>{selectedDoctors.length} selected</span>
                            <Button
                                text="Approve"
                                variant="success"
                                onClick={() => handleBulkAction('approve')}
                                disabled={loading}
                            />
                            <Button
                                text="Suspend"
                                variant="warning"
                                onClick={() => handleBulkAction('suspend')}
                                disabled={loading}
                            />
                            <Button
                                text="Delete"
                                variant="error"
                                onClick={() => handleBulkAction('delete')}
                                disabled={loading}
                            />
                        </div>
                    )}
                    <Button
                        icon={<img src={ExportIcon} alt="Export" />}
                        text="Export Data"
                        variant="outline"
                        onClick={handleExportData}
                    />
                </div>
            </div>

            <div className="filters-section">
                <div className="search-bar">
                    <img src={Search} alt="" />
                    <input
                        type="text"
                        placeholder="Search doctors..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                <div className="filters">
                    <select
                        value={filters.status}
                        onChange={(e) => setFilters({...filters, status: e.target.value})}
                    >
                        <option value="all">All Status</option>
                        <option value="active">Active</option>
                        <option value="suspended">Suspended</option>
                        <option value="pending">Pending Approval</option>
                    </select>

                    <select
                        value={filters.specialization}
                        onChange={(e) => setFilters({...filters, specialization: e.target.value})}
                    >
                        <option value="all">All Specializations</option>
                        <option value="fish-health">Fish Health Management</option>
                        <option value="aquaculture">Aquaculture Specialist</option>
                        <option value="disease">Disease Control</option>
                    </select>

                    <select
                        value={filters.experience}
                        onChange={(e) => setFilters({...filters, experience: e.target.value})}
                    >
                        <option value="all">All Experience</option>
                        <option value="0-2">0-2 years</option>
                        <option value="3-5">3-5 years</option>
                        <option value="5+">5+ years</option>
                    </select>
                </div>
            </div>

            <div className="doctors-table-container">
                <table className="doctors-table">
                    <thead>
                        <tr>
                            <th className="checkbox-column">
                                <input 
                                    type="checkbox" 
                                    onChange={handleSelectAll}
                                    checked={selectedDoctors.length === filteredDoctors.length && filteredDoctors.length > 0}
                                />
                            </th>
                            <th className="doctor-info-column">
                                <div className="th-content" onClick={() => requestSort('name')}>
                                    <span>Doctor</span>
                                    <img 
                                        src={Filter} 
                                        alt="Sort"
                                        className={sortConfig.key === 'name' ? sortConfig.direction : ''}
                                    />
                                </div>
                            </th>
                            <th onClick={() => requestSort('specialization')}>
                                <div className="th-content">
                                    <span>Specialization</span>
                                    <img 
                                        src={Filter} 
                                        alt="Sort"
                                        className={sortConfig.key === 'specialization' ? sortConfig.direction : ''}
                                    />
                                </div>
                            </th>
                            <th onClick={() => requestSort('experience')}>
                                <div className="th-content">
                                    <span>Experience</span>
                                    <img 
                                        src={Filter} 
                                        alt="Sort"
                                        className={sortConfig.key === 'experience' ? sortConfig.direction : ''}
                                    />
                                </div>
                            </th>
                            <th onClick={() => requestSort('totalCases')}>
                                <div className="th-content">
                                    <span>Cases</span>
                                    <img 
                                        src={Filter} 
                                        alt="Sort"
                                        className={sortConfig.key === 'totalCases' ? sortConfig.direction : ''}
                                    />
                                </div>
                            </th>
                            <th onClick={() => requestSort('ratings')}>
                                <div className="th-content">
                                    <span>Rating</span>
                                    <img 
                                        src={Filter} 
                                        alt="Sort"
                                        className={sortConfig.key === 'ratings' ? sortConfig.direction : ''}
                                    />
                                </div>
                            </th>
                            <th onClick={() => requestSort('revenue')}>
                                <div className="th-content">
                                    <span>Revenue</span>
                                    <img 
                                        src={Filter} 
                                        alt="Sort"
                                        className={sortConfig.key === 'revenue' ? sortConfig.direction : ''}
                                    />
                                </div>
                            </th>
                            <th onClick={() => requestSort('status')}>
                                <div className="th-content">
                                    <span>Status</span>
                                    <img 
                                        src={Filter} 
                                        alt="Sort"
                                        className={sortConfig.key === 'status' ? sortConfig.direction : ''}
                                    />
                                </div>
                            </th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredDoctors.length === 0 ? (
                            <tr>
                                <td colSpan="9" className="no-data">
                                    <div className="no-data-message">
                                        <p>No doctors found matching your criteria</p>
                                        {searchTerm || filters.status !== 'all' || 
                                         filters.specialization !== 'all' || filters.experience !== 'all' ? (
                                            <Button 
                                                text="Clear Filters" 
                                                variant="outline"
                                                onClick={() => {
                                                    setSearchTerm('');
                                                    setFilters({
                                                        status: 'all',
                                                        specialization: 'all',
                                                        experience: 'all'
                                                    });
                                                }}
                                            />
                                        ) : null}
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            filteredDoctors.map(doctor => (
                                <tr key={doctor.id} className={selectedDoctors.includes(doctor.id) ? 'selected' : ''}>
                                    <td className="checkbox-column">
                                        <input 
                                            type="checkbox" 
                                            checked={selectedDoctors.includes(doctor.id)}
                                            onChange={(e) => handleSelectDoctor(doctor.id, e.target.checked)}
                                        />
                                    </td>
                                    <td className="doctor-info-column">
                                        <div className="doctor-profile">
                                            <div className="doctor-info">
                                                <div className="doctor-name">{doctor.first_name +" "+ doctor.last_name}</div>
                                                {/* <div className="doctor-contact">
                                                    <div className="info-item">
                                                        <span>{doctor.email}</span>
                                                    </div>
                                                    <div className="info-item">
                                                        <span>{doctor.phone}</span>
                                                    </div>
                                                </div>
                                                <div className="doctor-license">
                                                    <span>License: {doctor.licenseNumber}</span>
                                                </div> */}
                                            </div>
                                        </div>
                                    </td>
                                    <td>{doctor.specialization}</td>
                                    <td>{doctor.experience}</td>
                                    <td className="cases-column">
                                        <div>{doctor.totalCases}
                                            <span className="cases-breakdown">
                                                <span className="active">{doctor.activeCases} active</span>
                                                <span className="completed">{doctor.completedCases} completed</span>
                                            </span>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="rating">
                                            <span className="rating-value">{doctor.ratings}</span>
                                            <span className="rating-stars">
                                                {Array(5).fill().map((_, i) => (
                                                    <span 
                                                        key={i} 
                                                        className={`star ${i < Math.floor(doctor.ratings) ? 'filled' : ''}`}
                                                    >
                                                        ★
                                                    </span>
                                                ))}
                                            </span>
                                        </div>
                                    </td>
                                    <td>{doctor.revenue}</td>
                                    <td>
                                        <span className={`status-badge ${doctor.is_verified}`}>
                                            {doctor.is_verified ? 'Verified' : 'Not Verified'}
                                        </span>
                                    </td>
                                    <td className="actions-cell">
                                        <div className="table-actions">
                                            <button 
                                                className="action-btn"
                                                onClick={() => setShowActionMenu(showActionMenu === doctor.id ? null : doctor.id)}
                                            >
                                                Actions 
                                                {/* <img src={ChevronDown} alt="Actions" /> */}
                                            </button>
                                            
                                            {showActionMenu === doctor.id && (
                                                <div className="actions-dropdown">
                                                    <button onClick={() => handleViewProfile(doctor.id)}>
                                                        <img src={Eye} alt="View" />
                                                        View Profile
                                                    </button>
                                                    <button onClick={() => handleViewCases(doctor.id)}>
                                                        <img src={Eye} alt="Cases" />
                                                        View Cases
                                                    </button>
                                                    {doctor.status === 'active' ? (
                                                        <button 
                                                            className="warning"
                                                            onClick={() => handleStatusChange(doctor.id, 'suspended')}
                                                        >
                                                            {/* <img src={X} alt="Suspend" /> */}
                                                            Suspend
                                                        </button>
                                                    ) : (
                                                        <button 
                                                            className="success"
                                                            onClick={() => handleStatusChange(doctor.id, 'active')}
                                                        >
                                                            <img src={Check} alt="Activate" />
                                                            Activate
                                                        </button>
                                                    )}
                                                    <button className="danger">
                                                        {/* <img src={Delete} alt="Delete" /> */}
                                                        Delete
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default AdminDoctors;